/**
 * Runtime Configuration Loader
 *
 * Загружает конфигурацию из /config.json endpoint при старте приложения.
 * Это позволяет менять настройки без пересборки frontend.
 * Норма: app-shell-shell-routing-R04, app-shell-configuration-R14/R15.
 */

export interface RuntimeConfig {
  /** Базовый URL для API запросов (опционально) */
  apiBaseUrl?: string;
  /** Base path для Vue Router (например, "/metabridge" для subpath развертывания) */
  basePath?: string;
}

let config: RuntimeConfig | null = null;

/**
 * Загружает runtime конфигурацию из backend
 * Кэширует результат для повторных вызовов
 */
export async function loadConfig(): Promise<RuntimeConfig> {
  if (config !== null) {
    return config;
  }

  try {
    // Ресурс относительно базы приложения, а не текущего вложенного маршрута.
    const response = await fetch('config.json');
    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
    }

    const json: unknown = await response.json();
    config = json as RuntimeConfig;
    return config;
  } catch (error) {
    console.warn('Failed to load runtime config, using defaults:', error);
    // Возвращаем пустую конфигурацию в случае ошибки
    const defaultConfig: RuntimeConfig = {};
    config = defaultConfig;
    return config;
  }
}

/**
 * Получает загруженную конфигурацию
 * @throws {Error} если конфигурация еще не загружена
 */
export function getConfig(): RuntimeConfig {
  if (config === null) {
    throw new Error('Config not loaded yet. Call loadConfig() first.');
  }
  return config;
}

/**
 * Проверяет, загружена ли конфигурация
 */
export function isConfigLoaded(): boolean {
  return config !== null;
}

/**
 * Сбрасывает конфигурацию (для тестов)
 */
export function resetConfig(): void {
  config = null;
}
